import React, { useEffect, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import HoverButton from "../../components/HoverButton";
import scroll1 from "../../../src/assets/EarthenSuiteImg/ES14.jpg";
import circle1 from "../../../src/assets/EarthenCircle.png";
import EastIconArrow from "../../../src/assets/east.svg";

gsap.registerPlugin(ScrollTrigger);

const HomeES = () => {
    const navigate = useNavigate();
    const textRef = useRef(null);
    const imageRef = useRef(null);
    const buttonRef = useRef(null);
    
        const [imagesLoaded, setImagesLoaded] = useState(0);
        const totalImages = 2;
    
        const handleImageLoad = () => {
            setImagesLoaded((prev) => prev + 1);
        };
    
        useEffect(() => {
            if (imagesLoaded === totalImages) {
                gsap.fromTo(
                    textRef.current,
                    { x: "-80%", opacity: 0 },
                    {
                        x: "0%",
                        opacity: 1,
                        duration: 1.2,
                        ease: "power2.out",
                        scrollTrigger: {
                            trigger: textRef.current,
                            start: "top 80%",
                            toggleActions: "play reverse play reverse",
                        },
                    }
                );
    
                // Animate image container (Zoom-in effect)
                gsap.fromTo(
                    imageRef.current,
                    { opacity: 0, scale: 0.8 },
                    {
                        opacity: 1,
                        scale: 1,
                        duration: 1,
                        ease: "power2.out",
                        scrollTrigger: {
                            trigger: imageRef.current,
                            start: "top 60%",
                            toggleActions: "play reverse play reverse",
                        },
                    }
                );
    
                // Animate button container (right to left)
                gsap.fromTo(
                    buttonRef.current,
                    { x: "100%", opacity: 0 },
                    {
                        x: "0%",
                        opacity: 1,
                        duration: 1.2,
                        ease: "power2.out",
                        scrollTrigger: {
                            trigger: buttonRef.current,
                            start: "top 80%",
                            toggleActions: "play reverse play reverse",
                        },
                    }
                );
            }
        }, [imagesLoaded]); // Re-run animation when images are fully loaded

    const styles = {
        container: {
            backgroundColor: "#FFDCCD",
            width: "100vw",
            height: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
            overflow: "hidden",
            padding: "5vh 0"
        },
        textContainer: {
            position: "absolute",
            top: "15%",
            left: "5%",
            color: "#2A2A2A",
            zIndex: 2,
            width: "300px",
        },
        title: {
            width: { xs: "200px", md: "300px", lg: "372px" },
            fontFamily: "'ZapfHumnst BT'",
            fontWeight: 400,
            whiteSpace: "normal",
            textTransform: "uppercase",
            wordBreak: "break-word",
            fontSize: { xs: "32px", md: "50px", lg: "74px" },
            lineHeight: { xs: "39.61px", md: "40px", lg: "91.6px" },
            letterSpacing: "0.03em",
            color: "#2A2A2A",
        },
        subtitle: {
            maxWidth: "292px",
            fontFamily: "'Sora', sans-serif",
            fontSize: { xs: "12px", lg: "16px" },
            fontWeight: 300,
            color: "#000000",
        },
        imageContainer: {
            position: "relative",
            width: "90vw",  // Adjust width dynamically
            maxWidth: "438px",
            height: "auto", // Let height be auto-adjusted based on width
            aspectRatio: "3 / 4", // Ensures consistent proportions
            minHeight: "300px", // Prevents it from shrinking too much on small screens
            maxHeight: "585px", // Limits height on large screens
        },

        image: {
            position: "absolute",
            width: "100%",
            height: "100%",
            objectFit: "cover",
            borderRadius: "300px 300px 0px 0px",
            zIndex: "1",
        },
        circle: {
            width: "175px",
            height: "175px",
            position: "absolute",
            objectFit: "cover",
            objectPosition: "center",
            bottom: "-50px",
            left: "0%",
            transform: "translateX(-50%)",
            zIndex: "0",
        },
        buttonContainer: {
            position: "absolute",
            bottom: "15%",
            right: "8%",
            zIndex: 2,
            width: "300px",
        },
        buttonIcon: {
            width: "25px",
            height: "25px",
            transform: "rotate(-50deg)",
        },
        description: {
            maxWidth: "292px",
            fontFamily: "'Sora', sans-serif",
            fontSize: { xs: "12px", lg: "16px" },
            fontWeight: 300,
            color: "#000000",
            marginTop: "20px",
        },
    };

    return (
        <Box sx={styles.container}>
            {/* Add refs to each section */}
            <div ref={textRef} style={styles.textContainer}>
                <Typography sx={styles.title}>THE EARTHEN SUITE</Typography>
                <Typography sx={styles.subtitle}>
                    Inspired by nature, featuring rustic textures and calming neutral tones.
                </Typography>
            </div>

            <div ref={imageRef} style={styles.imageContainer}>
                <img src={scroll1} alt="scroll animation" style={styles.image} />
                <img src={circle1} alt="scroll animation" style={styles.circle} />
            </div>

            <div ref={buttonRef} style={styles.buttonContainer}>
                <HoverButton
                    onClick={() => navigate("/the-earthen-suite")}
                    width={"132px"}
                    height={"50px"}
                    endIcon={
                        <img
                            src={EastIconArrow}
                            alt="east"
                            style={styles.buttonIcon}
                        />
                    }
                    btnText={"Step in"}
                    fillBgColor="#DDB0A5"
                />
                <Typography sx={styles.description}>
                    This serene retreat blends organic elements with modern comfort, offering a grounding and rejuvenating escape amidst a peaceful setting.
                    <br />
                    <br />
                    2000 sq. ft. • max. 4 guests
                </Typography>
            </div>
        </Box>
    );
};

export default HomeES;